import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Chip, IconButton, Tooltip,
  Divider, Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScienceIcon from '@mui/icons-material/Science';
import CloseIcon from '@mui/icons-material/Close';

const API = '/api/produccion';
const TIPOS = ['MATERIA_PRIMA', 'INSUMO', 'SEMITERMINADO'];
const FORM_INIT = {
  codigo: '', nombre: '', producto_id: '', cantidad_produccion: 1,
  unidad_medida_id: '', descripcion: '', tiempo_produccion_min: '', estado: 'ACTIVA'
};
const ING_INIT = { producto_id: '', cantidad: '', unidad_medida_id: '', tipo: 'MATERIA_PRIMA' };

function fmt(v) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function Recetas() {
  const [recetas, setRecetas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [productos, setProductos] = useState([]);
  const [unidades, setUnidades] = useState([]);

  const [dlgForm, setDlgForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_INIT);
  const [ingredientes, setIngredientes] = useState([]);
  const [saving, setSaving] = useState(false);

  const [dlgVer, setDlgVer] = useState(null);

  const [busqProducto, setBusqProducto] = useState('');
  const [ingBusq, setIngBusq] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/recetas`);
      setRecetas(r.data);
    } catch { setError('Error al cargar recetas'); }
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    axios.get('/api/productos?limit=500').then(r => setProductos(r.data)).catch(() => {});
    axios.get('/api/unidades').then(r => setUnidades(r.data)).catch(() => {});
  }, []);

  const prodOptions = productos.filter(p =>
    !busqProducto || p.nombre.toLowerCase().includes(busqProducto.toLowerCase()) ||
    (p.codigo || '').toLowerCase().includes(busqProducto.toLowerCase())
  ).slice(0, 60);

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function abrirNuevo() {
    setEditando(null); setForm(FORM_INIT); setIngredientes([]); setBusqProducto(''); setDlgForm(true);
  }

  async function abrirEditar(r) {
    setEditando(r);
    setForm({
      codigo: r.codigo, nombre: r.nombre, producto_id: r.producto_id,
      cantidad_produccion: r.cantidad_produccion, unidad_medida_id: r.unidad_medida_id || '',
      descripcion: r.descripcion || '', tiempo_produccion_min: r.tiempo_produccion_min || '',
      estado: r.estado
    });
    try {
      const det = await axios.get(`${API}/recetas/${r.id}`);
      setIngredientes(det.data.ingredientes.map(i => ({
        producto_id: i.producto_id, cantidad: i.cantidad,
        unidad_medida_id: i.unidad_medida_id || '', tipo: i.tipo,
        _nombre: i.insumo_nombre, _codigo: i.insumo_codigo
      })));
    } catch { setIngredientes([]); }
    setBusqProducto('');
    setDlgForm(true);
  }

  function addIngrediente() {
    setIngredientes(prev => [...prev, { ...ING_INIT }]);
  }
  function setIng(i, k, v) {
    setIngredientes(prev => prev.map((ing, idx) => idx === i ? { ...ing, [k]: v } : ing));
  }
  function removeIng(i) {
    setIngredientes(prev => prev.filter((_, idx) => idx !== i));
  }

  async function guardar() {
    if (!form.codigo || !form.nombre || !form.producto_id)
      return setError('Código, nombre y producto terminado son requeridos');
    if (ingredientes.length === 0)
      return setError('Agrega al menos un ingrediente/insumo');
    setSaving(true); setError('');
    try {
      const payload = { ...form, ingredientes };
      if (editando) {
        await axios.put(`${API}/recetas/${editando.id}`, payload);
        setSuccess('Receta actualizada');
      } else {
        await axios.post(`${API}/recetas`, payload);
        setSuccess('Receta creada');
      }
      setDlgForm(false); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al guardar'); }
    setSaving(false);
  }

  async function verDetalle(r) {
    try {
      const det = await axios.get(`${API}/recetas/${r.id}`);
      setDlgVer(det.data);
    } catch { setDlgVer(r); }
  }

  const productoTerminadoNombre = editando
    ? productos.find(p => p.id === editando.producto_id)?.nombre || ''
    : productos.find(p => p.id === form.producto_id)?.nombre || '';

  return (
    <Box sx={{ width: '100%', maxWidth: 1300, mx: 'auto', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <ScienceIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">Recetas de Producción</Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={cargar} disabled={loading}><RefreshIcon /></IconButton>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNuevo}>Nueva Receta</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                {['Código', 'Nombre', 'Producto Terminado', 'Cant. Producción', 'Ingredientes', 'Estado', 'Acc.'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {recetas.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay recetas registradas</TableCell></TableRow>
              ) : recetas.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.codigo}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{r.nombre}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.producto_nombre}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.producto_codigo}</Typography>
                  </TableCell>
                  <TableCell align="right">{fmt(r.cantidad_produccion)} {r.unidad_abreviatura || ''}</TableCell>
                  <TableCell align="center"><Chip label={`${r.total_ingredientes} items`} size="small" color="info" variant="outlined" /></TableCell>
                  <TableCell align="center">
                    <Chip label={r.estado} size="small" color={r.estado === 'ACTIVA' ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Ver detalle"><IconButton size="small" color="info" onClick={() => verDetalle(r)}><ScienceIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog CRUD Receta */}
      <Dialog open={dlgForm} onClose={() => setDlgForm(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {editando ? 'Editar Receta' : 'Nueva Receta'}
          <IconButton onClick={() => setDlgForm(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {/* Cabecera */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <TextField size="small" label="Código *" value={form.codigo} disabled={!!editando}
              onChange={e => setF('codigo', e.target.value.toUpperCase())} sx={{ width: '10em' }} />
            <TextField size="small" label="Nombre de la Receta *" value={form.nombre}
              onChange={e => setF('nombre', e.target.value)} sx={{ flex: 1, minWidth: '18em' }} />
            <TextField select size="small" label="Estado" value={form.estado}
              onChange={e => setF('estado', e.target.value)} sx={{ minWidth: '10em' }}>
              <MenuItem value="ACTIVA">Activa</MenuItem>
              <MenuItem value="INACTIVA">Inactiva</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
            <Autocomplete
              size="small" sx={{ minWidth: '22em', flex: 1 }}
              options={prodOptions}
              getOptionLabel={p => `${p.codigo ? p.codigo + ' - ' : ''}${p.nombre}`}
              value={productos.find(p => p.id === form.producto_id) || null}
              onChange={(_, v) => setF('producto_id', v?.id || '')}
              onInputChange={(_, v) => setBusqProducto(v)}
              renderInput={params => <TextField {...params} label="Producto terminado *" />}
            />
            <TextField size="small" label="Cant. por lote *" type="number"
              value={form.cantidad_produccion} onChange={e => setF('cantidad_produccion', e.target.value)}
              sx={{ width: '9em' }} />
            <TextField select size="small" label="Unidad" value={form.unidad_medida_id}
              onChange={e => setF('unidad_medida_id', e.target.value)} sx={{ minWidth: '10em' }}>
              <MenuItem value="">Sin unidad</MenuItem>
              {unidades.map(u => <MenuItem key={u.id} value={u.id}>{u.abreviatura} — {u.nombre}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Tiempo (min)" type="number"
              value={form.tiempo_produccion_min} onChange={e => setF('tiempo_produccion_min', e.target.value)}
              sx={{ width: '9em' }} />
          </Box>
          <TextField size="small" fullWidth label="Descripción / Instrucciones" multiline rows={2}
            value={form.descripcion} onChange={e => setF('descripcion', e.target.value)} sx={{ mb: 2 }} />

          <Divider sx={{ mb: 1.5 }}>
            <Typography variant="caption" fontWeight="bold">Ingredientes / Insumos</Typography>
          </Divider>

          {ingredientes.map((ing, i) => {
            const ingOptions = productos.filter(p =>
              !ingBusq || p.nombre.toLowerCase().includes(ingBusq.toLowerCase()) ||
              (p.codigo || '').toLowerCase().includes(ingBusq.toLowerCase())
            ).slice(0, 60);
            return (
              <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ minWidth: 20, color: 'text.secondary' }}>{i + 1}.</Typography>
                <Autocomplete
                  size="small" sx={{ flex: 1, minWidth: '18em' }}
                  options={ingOptions}
                  getOptionLabel={p => `${p.codigo ? p.codigo + ' - ' : ''}${p.nombre}`}
                  value={productos.find(p => p.id === ing.producto_id) || null}
                  onChange={(_, v) => setIng(i, 'producto_id', v?.id || '')}
                  onInputChange={(_, v) => setIngBusq(v)}
                  renderInput={params => <TextField {...params} label="Insumo / Materia prima *" />}
                />
                <TextField size="small" label="Cantidad *" type="number"
                  value={ing.cantidad} onChange={e => setIng(i, 'cantidad', e.target.value)} sx={{ width: '8em' }} />
                <TextField select size="small" label="Unidad" value={ing.unidad_medida_id}
                  onChange={e => setIng(i, 'unidad_medida_id', e.target.value)} sx={{ minWidth: '9em' }}>
                  <MenuItem value="">—</MenuItem>
                  {unidades.map(u => <MenuItem key={u.id} value={u.id}>{u.abreviatura}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Tipo" value={ing.tipo}
                  onChange={e => setIng(i, 'tipo', e.target.value)} sx={{ minWidth: '11em' }}>
                  {TIPOS.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
                </TextField>
                <IconButton size="small" color="error" onClick={() => removeIng(i)}><DeleteIcon fontSize="small" /></IconButton>
              </Box>
            );
          })}

          <Button size="small" startIcon={<AddIcon />} onClick={addIngrediente} sx={{ mt: 0.5 }}>
            Agregar Ingrediente
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (editando ? 'Actualizar' : 'Guardar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Ver Detalle */}
      <Dialog open={!!dlgVer} onClose={() => setDlgVer(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box><ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />{dlgVer?.nombre}</Box>
          <IconButton onClick={() => setDlgVer(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dlgVer && (
            <Box>
              <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2"><strong>Código:</strong> {dlgVer.codigo}</Typography>
                <Typography variant="body2"><strong>Producto terminado:</strong> {dlgVer.producto_nombre}</Typography>
                <Typography variant="body2"><strong>Lote:</strong> {fmt(dlgVer.cantidad_produccion)} {dlgVer.unidad_abreviatura || ''}</Typography>
                {dlgVer.tiempo_produccion_min && <Typography variant="body2"><strong>Tiempo:</strong> {dlgVer.tiempo_produccion_min} min</Typography>}
              </Box>
              {dlgVer.descripcion && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{dlgVer.descripcion}</Typography>}
              <Divider sx={{ mb: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Ingredientes ({dlgVer.ingredientes?.length || 0})</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Insumo</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell>Unidad</TableCell>
                    <TableCell>Tipo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(dlgVer.ingredientes || []).map((ing, i) => (
                    <TableRow key={i}>
                      <TableCell>{ing.insumo_nombre}<br /><Typography variant="caption" color="text.secondary">{ing.insumo_codigo}</Typography></TableCell>
                      <TableCell align="right">{fmt(ing.cantidad)}</TableCell>
                      <TableCell>{ing.unidad_abreviatura || '—'}</TableCell>
                      <TableCell><Chip label={ing.tipo?.replace('_', ' ')} size="small" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { if (dlgVer) { abrirEditar(dlgVer); setDlgVer(null); } }}>Editar</Button>
          <Button variant="contained" onClick={() => setDlgVer(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
